import React, { MouseEvent, useState } from 'react';
import { GoTriangleDown } from 'react-icons/go';

import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';

import {
  Box,
  Button,
  ClickAwayListener,
  Paper,
  Popper,
  styled,
  Typography,
} from '@mui/material';
import { DatePicker, DateValidationError } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { DateTimeFormats } from '../constants/dateFormats';

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

interface FilterDateProps {
  title: string;
  defaultValue: { fromDate: string; toDate: string };
  onFilterDateSelect: (minDate: string, maxDate: string) => void;
  maxAllowedDate?: Date;
  allowClear?: boolean;
  onClearDate?: () => void;
}

const FilterDateRange: React.FC<FilterDateProps> = ({
  title,
  defaultValue,
  onFilterDateSelect,
  maxAllowedDate,
  allowClear,
  onClearDate,
}) => {
  // local state is state specific for this component
  // the defaultValue is a redux state that is initialised on the FilterBar component
  const [localFromDate, setLocalFromDate] = useState<Dayjs | null>(
    defaultValue.fromDate ? dayjs(defaultValue.fromDate).startOf('day') : null,
  );
  const [localToDate, setLocalToDate] = useState<Dayjs | null>(
    defaultValue.toDate ? dayjs(defaultValue.toDate).startOf('day') : null,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fromDateError, setFromDateError] =
    useState<DateValidationError | null>(null);
  const [toDateError, setToDateError] = useState<DateValidationError | null>(
    null,
  );

  const open = Boolean(anchorEl);

  const isValid = Boolean(
    localFromDate &&
      localToDate &&
      localToDate.isSameOrAfter(localFromDate) &&
      !fromDateError &&
      !toDateError,
  );

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const handleTitleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSaveClick = () => {
    if (localFromDate && localToDate) {
      // This function calls the redux dispatch method and sync the state with the redux store
      // Convert local midnight to UTC
      // For Singapore (UTC+8), Feb 2 00:00 local → Feb 1 16:00 UTC
      onFilterDateSelect(
        localFromDate
          .startOf('day')
          .utc()
          .format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
        localToDate.endOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
      );
      handleClickAway();
    }
  };

  const onClearDateClick = () => {
    setLocalFromDate(null);
    setLocalToDate(null);

    // This function calls the redux dispatch method and sync the state with the redux store
    if (onClearDate) {
      onClearDate();
    }
    handleClickAway();
  };

  const getDateWithoutTime = (date: Dayjs) => {
    return date.startOf('day');
  };

  const renderButtonLabel = () => {
    if (localFromDate || localToDate) {
      const formattedMin = localFromDate
        ? dayjs(localFromDate).format(DateTimeFormats.SIMPLE_DATE)
        : 'Start Date';

      const formattedMax = localToDate
        ? dayjs(localToDate).format(DateTimeFormats.SIMPLE_DATE)
        : 'End Date';

      return (
        <Typography variant="body1">
          {formattedMin} - {formattedMax}
        </Typography>
      );
    }

    return <Typography variant="body1">{title}</Typography>;
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <StyledButton
          variant="text"
          size="large"
          disableRipple
          onClick={handleTitleClick}
        >
          <Typography
            display="flex"
            justifyContent="center"
            alignItems="center"
            color="textSecondary"
          >
            {renderButtonLabel()}
            <Box
              display="flex"
              justifyContent="center"
              ml={1}
              sx={{ rotate: open ? '180deg' : '0deg' }}
            >
              <GoTriangleDown />
            </Box>
          </Typography>
        </StyledButton>

        {open && (
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            disablePortal={false}
            sx={{ zIndex: 10 }}
          >
            <Box mt={1}>
              <Paper elevation={4}>
                <Box
                  p={2}
                  width={400}
                  overflow="visible"
                  display="flex"
                  flexDirection="column"
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <DatePicker
                        label="start date"
                        maxDate={
                          localToDate
                            ? dayjs(getDateWithoutTime(localToDate))
                            : maxAllowedDate
                              ? dayjs(maxAllowedDate)
                              : undefined
                        }
                        value={localFromDate}
                        onChange={newValue => {
                          setLocalFromDate(newValue);
                          setFromDateError(null);
                        }}
                        onError={reason => {
                          setFromDateError(reason);
                        }}
                        slotProps={{
                          textField: { variant: 'filled' },
                        }}
                      />
                      <Typography
                        color="textSecondary"
                        whiteSpace="nowrap"
                        mx={1}
                      >
                        to
                      </Typography>
                      <DatePicker
                        label="end date"
                        minDate={
                          localFromDate
                            ? getDateWithoutTime(localFromDate)
                            : undefined
                        }
                        maxDate={
                          maxAllowedDate ? dayjs(maxAllowedDate) : undefined
                        }
                        value={localToDate}
                        onChange={newValue => {
                          setLocalToDate(newValue);
                          setToDateError(null);
                        }}
                        onError={reason => {
                          setToDateError(reason);
                        }}
                        slotProps={{
                          textField: { variant: 'filled' },
                        }}
                      />
                    </Box>
                  </LocalizationProvider>
                  <Box
                    mb={1}
                    mt={3}
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={3}
                  >
                    <ActionButton
                      buttonType="cancel"
                      size="small"
                      onClick={handleClickAway}
                    >
                      <Typography>cancel</Typography>
                    </ActionButton>

                    <ActionButton
                      buttonType="save"
                      size="small"
                      onClick={handleSaveClick}
                      disabled={!isValid}
                    >
                      <Typography>save</Typography>
                    </ActionButton>

                    {allowClear && onClearDate && (
                      <ActionButton
                        buttonType="clear"
                        size="small"
                        onClick={onClearDateClick}
                      >
                        <Typography>clear date</Typography>
                      </ActionButton>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

type ButtonColorType = 'cancel' | 'save' | 'clear';

interface ActionButtonProps {
  buttonType: ButtonColorType;
}

const ActionButton = styled(Button)<ActionButtonProps>(({
  theme,
  buttonType,
}) => {
  let backgroundColor: string;

  switch (buttonType) {
    case 'cancel':
      backgroundColor = theme.customColors.NeutralsNight.Night60;
      break;
    case 'save':
      backgroundColor = theme.palette.primary.main;
      break;
    case 'clear':
      backgroundColor = theme.palette.error.light;
      break;
    default:
      backgroundColor = theme.palette.background.default;
  }

  return {
    color: theme.palette.common.white,
    backgroundColor,
    cursor: 'pointer',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      textDecoration: 'underline',
      opacity: '0.8',
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      color: theme.palette.common.white,
      backgroundColor,
      cursor: 'not-allowed',
    },
  };
});

const StyledButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  lineHeight: 'normal',
  border: `1px solid ${theme.customColors.NeutralsNight.Night20}`,
  height: '32px',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  '&:hover': {
    border: `1px solid ${theme.customColors.NeutralsNight.Night100}`,
  },
  '&.open': {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
  },
}));

export default FilterDateRange;
